/* eslint-disable max-lines-per-function */
import React, {useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Time, TimeInterval} from 'yougile-util'
import {Hub, RangePoint} from 'yougile-model'
import {Switcher} from '@components/common/base/switcher/switcher'
import {
  DateRange,
  DateRangePicker
} from '@components/common/date-picker/date-picker'
import {DropdownList} from '@components/common/dropdown-list/dropdown-list'
import {TimeValue} from '@components/time-value'
import {cn} from '@containers/html-utils'
import {globals} from '@extensions/globals-base'
import {DeadlineCtl} from '@extensions/sticker/controllers/deadline-ctl'
import {stickerCtl} from '@extensions/sticker/sticker'
import {Task} from '@extensions/task'
import {getTimerTime, isTimerViewActive} from '../deadline-view'

type TextColor = 'red' | 'default'
const TwTime = tw.div<{$textColor?: TextColor}>`
  font-normal
  leading-main
  text-12
  ${({$textColor = 'default'}) =>
    $textColor === 'default' ? 'text-secondary-old' : ''}
  ${({$textColor = 'default'}) =>
    $textColor === 'red' ? 'text-error-old' : ''}

`

export interface DeadlineEditFormData {
  range: DateRange
  fullRangeMode: boolean
  withTimeMode: boolean
  notifyBefore: number
}

interface Props {
  value: DeadlineEditFormData
  onChange: (value: DeadlineEditFormData) => void
  task: Hub | undefined
  blockedRangeSections?: RangePoint[]
  canEdit?: boolean
}

const getTimerCompletionTime = (
  completion: number,
  deadline: number
): string => {
  const timeInterval = TimeInterval.fromDates(completion, deadline).toString()

  return stickerCtl.Timer.formatTime({
    time: timeInterval, // e.g.: 0w1d5h0m0s
    by: globals.currentUserId, // used as a stub
    running: false, // used as a stub
    timestamp: Time.now() // used as a stub
  })
}

export const DeadlineEditForm = ({
  value,
  onChange,
  task,
  blockedRangeSections,
  canEdit = true
}: Props) => {
  //
  const [isPlus, setIsPlus] = useState(false)
  const [timeValue, setTimeValue] = useState<undefined | string>('')
  const onRangeChange = (range: DateRange) => onChange({...value, range})

  const onFullRangeModeChange = (fullRangeMode: boolean) => {
    const blockedPoints = task ? DeadlineCtl.getBlockedPoints(task) : []

    if (blockedPoints.includes('start')) {
      if (fullRangeMode) {
        onChange({
          ...value,
          fullRangeMode,
          range: {
            ...value.range,
            start: value.range.end ? new Date(value.range.end) : undefined
          }
        })
      } else {
        onChange({
          ...value,
          fullRangeMode,
          range: {...value.range, start: undefined}
        })
      }
    } else {
      onChange({
        ...value,
        fullRangeMode,
        range: {...value.range, start: undefined}
      })
    }
  }
  const onWithTimeModeChange = (withTimeMode: boolean) => {
    onChange({
      ...value,
      withTimeMode,
      notifyBefore: correctNotification(withTimeMode, value.notifyBefore)
    })
  }
  const onNotificationTimeChange = (hours: number) => {
    onChange({...value, notifyBefore: hoursToMs(hours)})
  }

  const {range, fullRangeMode, withTimeMode, notifyBefore} = value

  const notificationOptions = getNotificationOptions(withTimeMode)
  const selectedOption = notificationOptions.find(
    o => hoursToMs(o.value) === notifyBefore
  )
  const completionTime = Task.getCompletionTime(task)
  const completion = completionTime ?? Time.now()
  const deadlineTimestamp = Time.toTimestamp(range?.end ?? new Date())
  const deadline = withTimeMode
    ? deadlineTimestamp
    : Time.endOfDay(deadlineTimestamp) - Time.minute
  const lessThenDay = deadline - completion < Time.day
  const isTimerView = isTimerViewActive(task)
  const timerCompletionTime =
    lessThenDay &&
    isTimerView &&
    completionTime &&
    getTimerCompletionTime(completionTime, deadline)

  const startBlocked = blockedRangeSections?.includes('start')
  const endBlocked = blockedRangeSections?.includes('end')
  const finalBlockedSections = [
    ...(blockedRangeSections ?? []),
    ...(!canEdit && !startBlocked ? ['start' as RangePoint] : []),
    ...(!canEdit && !endBlocked ? ['end' as RangePoint] : [])
  ]

  const getValue = (val: string) => {
    if (val.startsWith('-')) {
      setIsPlus(false)
      setTimeValue(val.substring(1))
    } else {
      setIsPlus(true)
      setTimeValue(val)
    }
  }

  return (
    <div className="flex flex-col bg-main-old">
      {lessThenDay && isTimerView && !completionTime && (
        <div className="px-8 pt-12 pb-8">
          <TwTime $textColor={!isPlus ? 'red' : 'default'}>
            <span>
              {isPlus
                ? Lang.msg.myStickers.remainingBeforeDeadline
                : Lang.msg.myStickers.deadlineOverdueBy}
            </span>
            : {timeValue}
          </TwTime>
          <div className="hidden">
            <TimeValue
              value={() => getTimerTime(deadline)}
              getValue={getValue}
            />
          </div>
        </div>
      )}
      {lessThenDay && isTimerView && completionTime && (
        <div className="px-8 pt-12 pb-8">
          <TwTime $textColor={deadline > completion ? 'default' : 'red'}>
            <span>
              {deadline > completion
                ? Lang.msg.myStickers.remainingBeforeDeadline
                : Lang.msg.myStickers.deadlineOverdueBy}
            </span>
            :{' '}
            {deadline > completion
              ? timerCompletionTime
              : String(timerCompletionTime).substring(1)}
          </TwTime>
        </div>
      )}
      <div className="inline-flex p-10 py-4">
        <DateRangePicker
          value={range}
          hideRangeSection={fullRangeMode ? undefined : 'start'}
          onChange={onRangeChange}
          showTimeInput={withTimeMode}
          blockedRangeSections={finalBlockedSections}
        />
      </div>
      <div className="pb-8 border-0 border-t border-solid border-main-old pt-14">
        <PickerOption
          label={Lang.msg.withTime}
          value={withTimeMode}
          onChange={onWithTimeModeChange}
          disabled={!canEdit}
        />
        <PickerOption
          label={Lang.msg.stickerPanel.addStart}
          value={fullRangeMode}
          onChange={onFullRangeModeChange}
          disabled={!canEdit || startBlocked}
        />
        <PickerOption
          label={Lang.msg.stickerPanel.addNotification}
          value={notifyBefore > 0}
          onChange={selected =>
            onNotificationTimeChange(
              selected ? notificationOptions[0].value : 0
            )
          }
          disabled={!canEdit}
        >
          {selectedOption && (
            <div
              className={
                fullRangeMode ? 'inline-block pl-16 w-208 -mt-1' : 'block'
              }
            >
              <DropdownList
                options={notificationOptions}
                value={selectedOption.value ?? 0}
                onChange={onNotificationTimeChange}
                disabled={!canEdit || notifyBefore === 0}
                className="w-full"
              />
            </div>
          )}
        </PickerOption>
      </div>
    </div>
  )
}

const PickerOption = (props: {
  label: string
  value: boolean
  onChange: (value: boolean) => void
  children?: React.ReactElement
  disabled?: boolean
}) => {
  const {label, value, onChange, children, disabled} = props
  return (
    <div className="pl-16 pr-12">
      <div
        className={cn(
          'inline-flex items-center pb-10 w-220 shrink-0',
          !disabled && 'cursor-pointer'
        )}
        onClick={() => !disabled && onChange(!value)}
      >
        <div className="flex-grow text-14">{label}</div>
        <Switcher active={value} disabled={disabled} />
      </div>
      {children}
    </div>
  )
}

interface NotificationOption {
  value: number
  label: string
}

const correctNotification = (
  withTimeMode: boolean,
  prevNotifyBefore: number
): number => {
  if (withTimeMode || prevNotifyBefore === 0) {
    return prevNotifyBefore
  }
  const oneDay = hoursToMs(24)
  return prevNotifyBefore > oneDay ? prevNotifyBefore : oneDay
}

const getNotificationOptions = (withTime: boolean): NotificationOption[] => {
  const days = [1, 2, 3].map(day => ({
    value: day * 24,
    label: Lang.msg.severalDays(day)
  }))
  if (!withTime) {
    return days
  }
  const hours = [
    // (0.05 / 3) * 1,
    0.25, 0.5, 1, 1.5, 2, 3, 4, 5, 6, 8
  ].map(hour => {
    return {
      value: hour,
      label: getHoursLabel(hour)
    }
  })
  return hours.concat(days)
}

const getHoursLabel = (value: number) => {
  const minutes = (value % 1) * 60
  const hours = Math.floor(value)
  const result = [
    hours !== 0 ? Lang.msg.severalHours(hours) : '',
    minutes !== 0 ? Lang.msg.severalMinutes(minutes) : ''
  ]
  return result.filter(Boolean).join(' ')
}

const hoursToMs = (h: number) => h * 3600 * 1000
